import { useState, useCallback, useRef, useEffect, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import Link from 'next/link';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { DocSearchModal, useDocSearchKeyboardEvents } from '@docsearch/react';
import { DocSearchHit } from '@docsearch/react/dist/esm/types';

const ACTION_KEY_DEFAULT = [`Ctrl `, `Control`];
const ACTION_KEY_APPLE = [`⌘`, `Command`];

interface HitProps {
  children: ReactNode;
  hit: DocSearchHit;
}

function Hit({ hit, children }: HitProps) {
  return (
    <Link href={hit.url} prefetch={false}>
      <a>{children}</a>
    </Link>
  );
}

export function Search() {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const [initialQuery, setInitialQuery] = useState();
  const [browserDetected, setBrowserDetected] = useState(false);
  const [actionKey, setActionKey] = useState(ACTION_KEY_DEFAULT);

  const onOpen = useCallback(() => {
    setIsOpen(true);
  }, [setIsOpen]);

  const onClose = useCallback(() => {
    setIsOpen(false);
  }, [setIsOpen]);

  const onInput = useCallback(
    (e) => {
      setIsOpen(true);
      setInitialQuery(e.key);
    },
    [setIsOpen, setInitialQuery],
  );

  useDocSearchKeyboardEvents({
    isOpen,
    onOpen,
    onClose,
    onInput,
    searchButtonRef,
  });

  useEffect(() => {
    if (typeof navigator !== `undefined`) {
      if (/(Mac|iPhone|iPod|iPad)/i.test(navigator.platform)) {
        setActionKey(ACTION_KEY_APPLE);
      } else {
        setActionKey(ACTION_KEY_DEFAULT);
      }
      setBrowserDetected(true);
    }
  }, []);

  return (
    <>
      <Head>
        <link
          rel="preconnect"
          href="https://BH4D9OD16A-dsn.algolia.net"
          crossOrigin="true"
        />
      </Head>

      <button
        type="button"
        ref={searchButtonRef}
        onClick={onOpen}
        className="group leading-6 text-gray-500 font-medium flex items-center space-x-3 sm:space-x-4 hover:text-gray-700 transition-colors duration-200 w-full py-2 focus:outline-none focus:text-haiti"
      >
        <svg
          fill="none"
          className="w-5 h-5 md:w-6 md:h-6 text-primary-800 md:text-gray-500 group-hover:text-gray-500 transition-colors duration-200"
        >
          <path
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span className="hidden md:inline">Search the documentation</span>
        <span
          style={{ opacity: browserDetected ? `1` : `0` }}
          className="hidden md:block text-gray-400 text-sm leading-5 py-0.5 px-1.5 border border-gray-300 rounded-md"
        >
          <span className="sr-only">Press </span>
          <kbd className="font-sans">
            <abbr title={actionKey[1]} className="no-underline">
              {actionKey[0]}
            </abbr>
          </kbd>
          <span className="sr-only"> and </span>
          <kbd className="font-sans">K</kbd>
          <span className="sr-only"> to search</span>
        </span>
      </button>
      {isOpen &&
        createPortal(
          <DocSearchModal
            indexName={process.env.NEXT_PUBLIC_ALGOLIA_INDEX_NAME || ``}
            apiKey={process.env.NEXT_PUBLIC_ALGOLIA_KEY || ``}
            initialQuery={initialQuery}
            initialScrollY={window.scrollY}
            onClose={onClose}
            navigator={{
              navigate({ suggestionUrl }) {
                setIsOpen(false);
                router.push(suggestionUrl);
              },
              navigateNewTab({ suggestionUrl }) {
                const windowReference = window.open(
                  suggestionUrl,
                  `_blank`,
                  `noopener`,
                );

                if (windowReference) {
                  windowReference.focus();
                }
              },
              navigateNewWindow({ suggestionUrl }) {
                window.open(suggestionUrl, `_blank`, `noopener`);
              },
            }}
            hitComponent={Hit}
            transformItems={(items) =>
              items.map((item) => {
                // We transform the absolute URL into a relative URL to
                // leverage Next's preloading.
                const a = document.createElement(`a`);
                a.href = item.url;

                const hash = a.hash === `#content-wrapper` ? `` : a.hash;

                return {
                  ...item,
                  url: `${a.pathname}${hash}`,
                };
              })
            }
          />,
          document.body,
        )}
    </>
  );
}
